/**
 * @file        src/modals/BookingInfoModal.jsx
 * @description "Meals / Cancellation / Confirmation / Payment Type" info modal.
 *              Driven by the live v3 room option passed in via
 *              nh:open-modal { name: 'bookingInfo', payload: { option, roomName } }.
 *              Reads option.mealBasis / inclusions / cancellation / compliance /
 *              pricing to describe the chosen rate. Falls back to generic copy
 *              when no option is supplied (e.g. opened without context).
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-22 Claude — Initial port. Static content.
 *   - 2026-06-24 Claude — Made dynamic from the live v3 option (meals, board,
 *                         cancellation window, PAN/passport, prepay).
 */
import { useEffect } from 'react';
import { inr } from '../lib/format.js';

function fmtDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const day = d.getDate();
  const mon = d.toLocaleString('en-US', { month: 'short' });
  const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day} ${mon} ${d.getFullYear()}, ${time}`;
}

function openCancellation(cancellation, roomName) {
  window.dispatchEvent(new CustomEvent('nh:open-modal', { detail: { name: 'cancellation', payload: { cancellation, roomName } } }));
}

export default function BookingInfoModal({ open, onClose, payload }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const option = payload?.option || null;
  const roomName = payload?.roomName || 'Room';

  // ---- Meals ----------------------------------------------------------------
  const mealBasis = (option?.mealBasis || '').trim();
  const inclusions = Array.isArray(option?.inclusions) ? option.inclusions.filter(Boolean) : [];
  const mealsIncluded = mealBasis && !/room\s*only|no\s*meal/i.test(mealBasis);

  // ---- Cancellation ---------------------------------------------------------
  const cancellation = option?.cancellation || null;
  const refundable = cancellation ? Boolean(cancellation.isRefundable) : null;
  const penalties = Array.isArray(cancellation?.penalties) ? cancellation.penalties : [];
  // Free-cancellation deadline = end of the first zero-charge window.
  const freeWindow = penalties.find((p) => Number(p?.amount ?? p?.charge ?? 1) === 0);
  const freeUntil = freeWindow ? fmtDateTime(freeWindow.to ?? freeWindow.toDate) : null;

  // ---- Payment / compliance -------------------------------------------------
  const panRequired = Boolean(option?.compliance?.panRequired);
  const passportRequired = Boolean(option?.compliance?.passportRequired);
  const total = option?.pricing ? Number(option.pricing.totalPrice ?? option.pricing.FP ?? 0) : 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel p-6 no-scrollbar">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>

        {/* Meals */}
        <div className="flex items-center gap-2 mb-3 pr-10">
          <h2 className="font-display text-xl font-extrabold">Meals</h2>
          {mealBasis && (
            <span className="px-2 py-0.5 rounded-full bg-neon/15 text-neon text-xs font-bold">{mealBasis}</span>
          )}
        </div>
        {mealsIncluded ? (
          <p className="text-sm text-muted">{mealBasis} is included in this rate.</p>
        ) : (
          <p className="text-sm text-muted">Meals not included.</p>
        )}
        {inclusions.length > 0 && (
          <ul className="mt-2 space-y-1">
            {inclusions.map((it) => (
              <li key={it} className="flex items-center gap-1.5 text-sm text-muted">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neon shrink-0"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
                {it}
              </li>
            ))}
          </ul>
        )}

        {/* Cancellation */}
        <h3 className="font-display font-bold mt-6 mb-1.5">Cancellation</h3>
        {refundable === null ? (
          <p className="text-sm text-muted">Cancellation terms vary by rate.</p>
        ) : refundable ? (
          <p className="text-sm text-muted">
            {freeUntil
              ? <>Free cancellation until <b className="text-ink">{freeUntil}</b>. After that, a cancellation fee applies.</>
              : 'This rate is refundable. Cancellation fees may apply closer to check-in.'}
          </p>
        ) : (
          <p className="text-sm text-muted">This booking cannot be modified, and no refund will be given if you cancel it. You'll be charged the cancellation fee if you don't check in.</p>
        )}
        {penalties.length > 0 && (
          <button
            onClick={() => openCancellation(cancellation, roomName)}
            className="mt-2 text-sm text-neon font-semibold hover:text-ink cursor-pointer"
          >
            View full cancellation policy →
          </button>
        )}

        {/* Confirmation */}
        <h3 className="font-display font-bold mt-5 mb-1.5">Confirmation</h3>
        <p className="text-sm text-muted">Your booking will be confirmed instantly.</p>

        {/* Payment Type */}
        <h3 className="font-display font-bold mt-5 mb-1.5">Payment Type</h3>
        <p className="text-sm text-muted">
          Prepay the room rate online{total ? <> — <b className="text-ink">{inr(total)}</b> total</> : ''}.
        </p>
        {panRequired && (
          <p className="text-sm text-muted mt-1">PAN card details are required to complete this booking.</p>
        )}
        {passportRequired && (
          <p className="text-sm text-muted mt-1">Passport details are required to complete this booking.</p>
        )}

        <p className="text-xs text-muted mt-6 pt-4 border-t border-line">Provided by our partners</p>
      </div>
    </div>
  );
}
